import { ROLES, PLANS } from "./permissions.constants";

/**
 * SECTION DEFINITIONS
 * Used by Dashboard for grouping
 */
export const FEATURE_SECTIONS = [
  { key: "general", label: "General" },
  { key: "player", label: "Player" },
  { key: "coach", label: "Coach" },
  { key: "admin", label: "Admin" },
];

/**
 * SINGLE SOURCE OF TRUTH
 * Dashboard + ComparisonPage + Access Control
 */
export const FEATURES = [

  // =================================================
  // ================= GENERAL =======================
  // =================================================
  {
    id: "attendance",
    name: "Attendance",
    section: "general",
    isCoachProvided: true,
    icon: "FaCalendarCheck",
    routes: {
      player: "/player-attendance",
      coach: "/admin-attendance",
      admin: "/admin-attendance",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH, ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "activities",
    name: "Activities",
    section: "general",
    icon: "FaCalendarDay",
    routes: {
      player: "/player-activities",
      coach: "/coach-activities",
      admin: "/other-coaches-list",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH, ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "performance-review",
    name: "Performance Review",
    section: "general",
    icon: "FaChartLine",
    routes: {
      player: "/performance-review/:userId",
      coach: "/admin-performance-review-landing",
      admin: "/admin-performance-review-landing",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH, ROLES.ADMIN],
      minPlan: PLANS.PRO,
    },
  },

  {
    id: "scoreboard",
    name: "Scoreboard",
    section: "general",
    icon: "FaListOl",
    routes: {
      player: "/coming-soon",
      coach: "/coming-soon",
      admin: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH, ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  // =================================================
  // ================= PLAYER ========================
  // =================================================
  {
    id: "book-coach",
    name: "Book a Coach",
    section: "player",
    icon: "FaUserPlus",
    routes: {
      player: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER],
      minPlan: PLANS.FREE,
      requiresCoach: true,
      blockReason: "You need a coach to use this feature",
    },
  },

  {
    id: "my-periodisation",
    name: "My Periodisation Plan",
    section: "player",
    icon: "FaUserClock",
    routes: {
      player: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER],
      minPlan: PLANS.PREMIUM,
    },
  },

  {
    id: "training-log",
    name: "My Training Log",
    section: "player",
    icon: "FaEdit",
    routes: {
      player: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "fitness-log",
    name: "My Fitness Log",
    section: "player",
    icon: "FaRunning",
    routes: {
      player: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "player-schedule",
    name: "My Schedule",
    section: "player",
    icon: "FaCalendar",
    routes: {
      player: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "player-classes",
    name: "My Classes",
    section: "player",
    icon: "FaChalkboardTeacher",
    routes: {
      player: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER],
      minPlan: PLANS.FREE,
    },
  },

  // =================================================
  // ================= COACH =========================
  // =================================================
  {
    id: "coach-pt-logs",
    name: "Coach 1 to 1 Logs",
    section: "coach",
    isCoachProvided: true,
    icon: "FaBook",
    routes: {
      player: "/pt-logs-list",
      coach: "/pt-logs-player-list",
      admin: "/pt-logs-player-list",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH, ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "coach-periodisation",
    name: "Coach Periodisation Plan",
    section: "coach",
    isCoachProvided: true,
    icon: "FaCalendarAlt",
    routes: {
      coach: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "coach-fitness-record",
    name: "Coach Fitness Record",
    section: "coach",
    isCoachProvided: true,
    icon: "FaClipboardList",
    routes: {
      coach: "/coming-soon",
    },
    access: {
      roles: [ROLES.PLAYER, ROLES.COACH],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "coach-schedule",
    name: "My Schedule",
    section: "coach",
    icon: "FaCalendar",
    routes: {
      coach: "/coming-soon",
    },
    access: {
      roles: [ROLES.COACH],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "coach-classes",
    name: "My Classes",
    section: "coach",
    icon: "FaChalkboardTeacher",
    routes: {
      coach: "/coming-soon",
    },
    access: {
      roles: [ROLES.COACH],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "coach-availability",
    name: "My Availability",
    section: "coach",
    icon: "FaClock",
    routes: {
      coach: "/coming-soon",
    },
    access: {
      roles: [ROLES.COACH],
      minPlan: PLANS.PRO,
    },
  },

  {
    id: "coach-leave",
    name: "Apply Leave",
    section: "coach",
    icon: "MdEventAvailable",
    routes: {
      coach: "/coach-leaves-list",
    },
    access: {
      roles: [ROLES.COACH],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "manage-players",
    name: "Manage Players",
    section: "coach",
    icon: "FaUsersCog",
    routes: {
      coach: "/manage-players",
    },
    access: {
      roles: [ROLES.COACH],
      minPlan: PLANS.FREE,
    },
  },

  // =================================================
  // ================= ADMIN =========================
  // =================================================
  {
    id: "manage-activities",
    name: "Manage Activities",
    section: "admin",
    icon: "FaTasks",
    routes: {
      admin: "/manage-activities",
    },
    access: {
      roles: [ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "manage-subscription",
    name: "Manage Subscription",
    section: "admin",
    icon: "FaCrown",
    routes: {
      admin: "/subscription",
    },
    access: {
      roles: [ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "manage-leave-reasons",
    name: "Manage Coach Leave Reasons",
    section: "admin",
    icon: "FaCalendarTimes",
    routes: {
      admin: "/manage-coach-leaves-reason",
    },
    access: {
      roles: [ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },

  {
    id: "manage-holidays",
    name: "Manage Holidays",
    section: "admin",
    icon: "LuPartyPopper",
    routes: {
      admin: "/manage-holidays",
    },
    access: {
      roles: [ROLES.ADMIN],
      minPlan: PLANS.FREE,
    },
  },
];
